import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import InstitutionNavbar from '../components/instituteNavbar';
import Footer from '../components/Footer';
import '../styles/GlobalStyles.css';

import { Edit, Trash2 } from 'lucide-react';

const ManageWalkins = () => {
  const [walkins, setWalkins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchWalkins = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/institutionfilter/walkins`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setWalkins(response.data.data || []);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching walkins:", err);
        setError('Failed to load walkin drives.');
        setLoading(false);
      }
    };
    fetchWalkins();
  }, []);

  const handleCreateWalkin = () => {
    navigate('/institute/walkin-post');
  };

  const handleEditWalkin = (walkin) => {
    navigate('/institute/walkin-post', { state: { mode: 'edit', walkinData: walkin } });
  };

  const handleDeleteWalkin = async (id) => {
    if (!window.confirm("Are you sure you want to delete this walk-in drive?")) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/institutionfilter/walkin/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWalkins(prev => prev.filter(w => w._id !== id));
    } catch (err) {
      console.error("Error deleting walkin:", err);
      alert('Failed to delete walkin drive.');
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      <InstitutionNavbar />

      <main
        className="page-container mt-[120px] min-h-screen overflow-visible"
      >
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">Manage Walk-In Drives</h1>
          <button
            onClick={handleCreateWalkin}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            + Post New Walk-In
          </button>
        </div>

        {loading ? (
          <div className="text-center py-10">Loading drives...</div>
        ) : error ? (
          <div className="text-center text-red-600 py-10">{error}</div>
        ) : walkins.length === 0 ? (
          <div className="text-center py-10 bg-white rounded-lg shadow">
            <p className="text-gray-500 mb-4">You haven't posted any walk-in drives yet.</p>
            <button
              onClick={handleCreateWalkin}
              className="text-blue-600 hover:underline"
            >
              Get started by posting a walk-in drive
            </button>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block bg-white rounded-xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-gray-100 border-b border-gray-200">
                    <tr>
                      <th className="p-4 font-semibold text-gray-600">Drive Title</th>
                      <th className="p-4 font-semibold text-gray-600">Date & Time</th>
                      <th className="p-4 font-semibold text-gray-600">Venue</th>
                      <th className="p-4 font-semibold text-gray-600">Positions</th>
                      <th className="p-4 font-semibold text-gray-600 text-right">Actions</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {walkins.map((walkin) => (
                      <tr key={walkin._id} className="hover:bg-gray-50 transition">
                        <td className="p-4">
                          <p className="font-bold text-gray-800">{walkin.heading}</p>
                        </td>
                        <td className="p-4 text-gray-600">
                          <p>{new Date(walkin.date).toLocaleDateString()}</p>
                          <p className="text-xs text-gray-500">{walkin.time}</p>
                        </td>
                        <td className="p-4 text-gray-600 truncate max-w-xs" title={walkin.venue}>
                          {walkin.venue}
                        </td>
                        <td className="p-4 text-gray-600">
                          {walkin.positions}
                        </td>
                        <td className="p-4 text-right">
                          <button
                            onClick={() => handleEditWalkin(walkin)}
                            className="text-blue-600 hover:text-blue-800 transition"
                            title="Edit Walk-In"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleDeleteWalkin(walkin._id)}
                            className="text-red-600 hover:text-red-800 transition ml-3"
                            title="Delete Walk-In"
                          >
                            <Trash2 size={18} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {walkins.map((walkin) => (
                <div
                  key={walkin._id}
                  className="bg-white rounded-lg shadow p-4 border border-gray-100 relative"
                >
                  <button
                    onClick={() => handleEditWalkin(walkin)}
                    className="absolute top-4 right-4 text-blue-600 hover:text-blue-800"
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    onClick={() => handleDeleteWalkin(walkin._id)}
                    className="absolute top-4 right-12 text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={18} />
                  </button>
                  <h2 className="font-semibold text-lg text-gray-800 mb-2 pr-8">
                    {walkin.heading}
                  </h2>

                  <div className="text-sm text-gray-600 space-y-1">
                    <p>
                      <span className="font-medium">Date:</span>{" "}
                      {new Date(walkin.date).toLocaleDateString()}
                    </p>
                    <p>
                      <span className="font-medium">Time:</span> {walkin.time}
                    </p>
                    <p>
                      <span className="font-medium">Venue:</span>
                      <span className="block text-gray-700">{walkin.venue}</span>
                    </p>
                    <p>
                      <span className="font-medium">Positions:</span> {walkin.positions}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default ManageWalkins;
